/**
 * AgentAnswerMeta — compact AgentCore evidence/status surface under AI answers
 */

import {
  Activity,
  ChevronDown,
  ChevronRight,
  Database,
  History,
  ListChecks,
  ThumbsUp,
} from 'lucide-react';
import { useState } from 'react';
import type { AgentFeedbackRating, AgentRunMeta } from '../../../features/chat/api';
import styles from '../AiConsultantPage.module.css';

interface AgentAnswerMetaProps {
  messageId: string;
  meta?: AgentRunMeta;
  feedbackPending?: boolean;
  onAskFollowup?: (value: string) => void;
  onFeedback?: (messageId: string, value: AgentFeedbackRating) => void;
}

const FRIENDLY_MISSING_TOPICS: Record<string, string> = {
  blood_pressure: '血压记录',
  diagnostic_report: '检查报告',
  general_health: '基础健康档案',
  glucose: '血糖/胰岛素',
  hemorheology: '血流变',
  homocysteine: '同型半胱氨酸',
  kidney_urinary: '肾尿路证据',
  lipids: '血脂相关指标',
  liver: '肝功能',
  observations: '检验指标',
  report: '体检报告',
  uric_acid: '尿酸',
  vascular_imaging: '血管影像/检查',
};

function positiveNumber(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

function evidenceKindLabel(kind?: string | null): string {
  if (kind === 'observation') return '指标';
  if (kind === 'diagnostic_report') return '报告';
  if (kind === 'summary') return '摘要';
  if (kind === 'tool_result') return '工具';
  return '证据';
}

function formatEvidenceDate(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  const dateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return trimmed;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatEvidenceSummary(summary?: string | null, date?: string | null): string | undefined {
  const trimmed = summary?.trim();
  if (!trimmed) return undefined;

  const formattedDate = formatEvidenceDate(date);
  let text = trimmed.replace(
    /\b(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?\b/g,
    formattedDate ? '' : '$1',
  );

  if (formattedDate) {
    text = text.replace(new RegExp(`\\b${escapeRegExp(formattedDate)}\\b`, 'g'), '');
  }

  return text.replace(/\s{2,}/g, ' ').trim() || undefined;
}

function formatMissingTopic(topic: string): string | undefined {
  const trimmed = topic.trim();
  if (!trimmed) return undefined;
  if (FRIENDLY_MISSING_TOPICS[trimmed]) return FRIENDLY_MISSING_TOPICS[trimmed];
  if (/^Observation\?code=/i.test(trimmed)) return undefined;
  if (/^[A-Za-z_]+$/.test(trimmed)) return undefined;
  return trimmed;
}

export function AgentAnswerMeta({
  messageId,
  meta,
  feedbackPending,
  onAskFollowup,
  onFeedback,
}: AgentAnswerMetaProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (!meta) return null;

  const stats = meta.agent;
  const historyCount = positiveNumber(stats?.historyCount);
  const healthRecordCount =
    positiveNumber(stats?.observationCount) +
    positiveNumber(stats?.diagnosticReportCount);
  const evidenceCount =
    positiveNumber(stats?.mandatoryEvidenceCount) ||
    positiveNumber(stats?.evidenceCardCount) ||
    (meta.evidenceCards?.length ?? 0);
  const toolCount = positiveNumber(stats?.toolCount);
  const quickPrompts = meta.quickPrompts ?? [];
  const evidenceCards = meta.evidenceCards ?? [];
  const missingTopics = (stats?.missingTopics ?? [])
    .map(formatMissingTopic)
    .filter((topic): topic is string => Boolean(topic));
  const hasEvidenceDetails = evidenceCards.length > 0 || missingTopics.length > 0;
  const hasStatus = historyCount > 0 || healthRecordCount > 0 || evidenceCount > 0 || toolCount > 0;
  const feedbackValue = meta.feedback?.rating;

  if (!hasStatus && evidenceCards.length === 0 && quickPrompts.length === 0 && missingTopics.length === 0) {
    return null;
  }

  return (
    <div className={styles.agentMeta} data-export-hide>
      {hasStatus && (
        <div className={styles.agentStatusStrip}>
          {historyCount > 0 && (
            <span className={styles.agentStatusItem}>
              <History className={styles.agentStatusIcon} />
              参考 {historyCount} 条历史
            </span>
          )}
          {healthRecordCount > 0 && (
            <span className={styles.agentStatusItem}>
              <Database className={styles.agentStatusIcon} />
              读取 {healthRecordCount} 项档案
            </span>
          )}
          {evidenceCount > 0 && (
            <button
              type="button"
              className={styles.agentStatusButton}
              onClick={() => setDetailsOpen((value) => !value)}
              disabled={!hasEvidenceDetails}
              aria-expanded={hasEvidenceDetails ? detailsOpen : undefined}
              aria-label={detailsOpen ? '收起档案依据' : '展开档案依据'}
              title={detailsOpen ? '收起档案依据' : '展开档案依据'}
            >
              <ListChecks className={styles.agentStatusIcon} />
              档案证据 {evidenceCount} 条
              {hasEvidenceDetails && (
                detailsOpen
                  ? <ChevronDown className={styles.agentStatusIcon} />
                  : <ChevronRight className={styles.agentStatusIcon} />
              )}
            </button>
          )}
          {toolCount > 0 && (
            <span className={styles.agentStatusItem}>
              <Activity className={styles.agentStatusIcon} />
              调用 {toolCount} 个工具
            </span>
          )}
        </div>
      )}

      {hasEvidenceDetails && detailsOpen && (
        <div className={styles.agentEvidencePanel}>
          {evidenceCards.length > 0 && (
            <div className={styles.evidenceList}>
              {evidenceCards.slice(0, 4).map((card, idx) => {
                const summary = formatEvidenceSummary(card.summary, card.date);
                const date = formatEvidenceDate(card.date);
                return (
                  <div key={`${card.title}-${idx}`} className={styles.evidenceItem}>
                    <div className={styles.evidenceHeader}>
                      <span className={styles.evidenceKind}>{evidenceKindLabel(card.kind)}</span>
                      <span className={styles.evidenceTitle}>{card.title || card.source || '证据'}</span>
                    </div>
                    {summary && <div className={styles.evidenceSummary}>{summary}</div>}
                    {(date || card.referenceRange) && (
                      <div className={styles.evidenceFootnote}>
                        {date && <span>采样 {date}</span>}
                        {card.referenceRange && <span>参考 {card.referenceRange}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {missingTopics.length > 0 && (
            <div className={styles.agentMissingTopics}>
              可补充：{missingTopics.slice(0, 4).join('、')}
            </div>
          )}

          {evidenceCards.length > 4 && (
            <div className={styles.agentEvidenceMore}>
              还有 {evidenceCards.length - 4} 条档案证据已用于回答
            </div>
          )}
        </div>
      )}

      {quickPrompts.length > 0 && (
        <div className={styles.agentFollowups}>
          {quickPrompts.slice(0, 3).map((prompt) => (
            <button
              key={prompt}
              type="button"
              className={styles.agentFollowupChip}
              onClick={() => onAskFollowup?.(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {onFeedback && (
        <div className={styles.agentFeedback}>
          <button
            type="button"
            className={`${styles.agentFeedbackButton} ${feedbackValue === 'helpful' ? styles.agentFeedbackButtonActive : ''}`}
            onClick={() => onFeedback(messageId, 'helpful')}
            disabled={feedbackPending}
            aria-pressed={feedbackValue === 'helpful'}
            aria-label="标记有帮助"
            title="有帮助"
          >
            <ThumbsUp className={styles.agentFeedbackIcon} />
            {feedbackPending && feedbackValue === 'helpful' ? '提交中' : feedbackValue === 'helpful' ? '已标记' : '有帮助'}
          </button>
        </div>
      )}
    </div>
  );
}
